import { toast } from "react-toastify";
import { isIPad } from "../utils/isIPad";
import axios from "axios";

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  // Pad the string with '=' if needed
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding)
    .replace(/\-/g, "+") // Replace '-' with '+'
    .replace(/_/g, "/"); // Replace '_' with '/'

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

// export async function setupNotification(userId, restaurantId) {
//   if (!("serviceWorker" in navigator)) {
//     console.warn("Service worker not supported.");
//     return;
//   }

//   const permission = await Notification.requestPermission();
//   if (permission !== "granted") {
//     console.warn("Notification permission not granted.");
//     return;
//   }

//   console.log("Registering Service Worker...");
//   await navigator.serviceWorker.register("/service-worker.js");

//   console.log("Waiting for Service Worker to be ready...");
//   const swRegistration = await navigator.serviceWorker.ready;
//   console.log("Service Worker is ready:", swRegistration);

//   const existingSub = await swRegistration.pushManager.getSubscription();

//   if (existingSub) {
//     console.log("Subscription already exists, skipping new creation.");
//     return;
//   }

//   const vapidKey = process.env.REACT_APP_VAPID_PUBLIC_KEY;
//   console.log("Using VAPID Key:", vapidKey);

//   const rawSubscription = await swRegistration.pushManager.subscribe({
//     userVisibleOnly: true,
//     applicationServerKey: urlBase64ToUint8Array(vapidKey),
//   });

//   const subscription = {
//     endpoint: rawSubscription.endpoint,
//     p256dh: btoa(
//       String.fromCharCode.apply(
//         null,
//         new Uint8Array(rawSubscription.getKey("p256dh"))
//       )
//     ),
//     auth: btoa(
//       String.fromCharCode.apply(
//         null,
//         new Uint8Array(rawSubscription.getKey("auth"))
//       )
//     ),
//   };

//   await fetch(`${process.env.REACT_APP_SECRET}/api/PWA/subscribe`, {
//     method: "POST",
//     headers: { "Content-Type": "application/json" },
//     body: JSON.stringify({
//       subscription,
//       userId,
//       restaurantId,
//     }),
//   });

//   console.log("Subscription successfully saved to backend!");
// }

// export async function waitForServiceWorker() {
//   let registration = navigator.serviceWorker.controller;

//   while (!registration) {
//     console.log("Waiting for service worker to be ready...");
//     await new Promise((resolve) => setTimeout(resolve, 1000));
//     registration = navigator.serviceWorker.controller;
//   }

//   return await navigator.serviceWorker.ready;
// }

// Helper: wait for service worker to be active (Brave/Opera/iPad fix)
export async function waitForServiceWorker() {
  if (!navigator.serviceWorker) return null;

  // Wait for registration to exist
  let registration = await navigator.serviceWorker.getRegistration();
  while (!registration) {
    await new Promise((res) => setTimeout(res, 200));
    registration = await navigator.serviceWorker.getRegistration();
  }

  // Wait until the active worker is ready
  if (registration.active && registration.active.state === "activated") {
    return registration;
  }

  // Otherwise wait for activation
  await new Promise((resolve) => {
    const sw =
      registration.installing || registration.waiting || registration.active;

    if (!sw) return resolve();

    if (sw.state === "activated") return resolve();

    sw.addEventListener("statechange", () => {
      if (sw.state === "activated") resolve();
    });
  });

  return registration;
}

// export async function setupNotification(userId, restaurantId) {
//   if (!("serviceWorker" in navigator)) {
//     // console.warn("Service Worker not supported.");
//     toast.warn("Service Worker not supported.");
//     return;
//   }

//   // Ensure SW is registered
//   await navigator.serviceWorker.register("/service-worker.js");

//   // Wait until active
//   const swRegistration = await waitForServiceWorker();

//   // --- iPAD SPECIAL HANDLING ---
//   if (isIPad()) {
//     // console.log(
//     //   "iPad detected. Waiting for user gesture to request permission."
//     // );
//     toast.info("Tap anywhere to enable notifications.");

//     // Safari requires permission to be triggered by a click/tap
//     document.body.addEventListener("click", async function handleClick() {
//       const permission = await Notification.requestPermission();

//       if (permission === "granted") {
//         await subscribeUser(swRegistration, userId, restaurantId);
//       } else {
//         // console.warn("iPad: Notification permission denied.");
//         toast.warn("Notification permission denied.");
//       }

//       document.body.removeEventListener("click", handleClick);
//     });

//     return; // STOP here for iPad until user taps
//   }

//   // --- DESKTOP BROWSERS (Chrome/Brave/Opera/Firefox) ---
//   const permission = await Notification.requestPermission();
//   if (permission !== "granted") {
//     // console.warn("Notification permission denied.");
//     toast.warn("Notification permission denied.");
//     return;
//   }

//   await subscribeUser(swRegistration, userId, restaurantId);
// }

// =============== COMMON SUBSCRIBE LOGIC FOR ALL DEVICES ==================

export async function setupNotification(userId, restaurantId) {
  if (!("serviceWorker" in navigator)) {
    toast.warn("Notifications not supported on this device.");
    return false; // Indicate failure
  }

  // Register SW if needed
  await navigator.serviceWorker.register("/service-worker.js");
  const swRegistration = await waitForServiceWorker();

  const reg = await navigator.serviceWorker.getRegistration();
  await reg.update();

  // Check if we're on iOS/iPadOS
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

  let permission;
  try {
    if (isIOS) {
      // On iOS, permission MUST be requested in direct response to user gesture
      // So this function must be called from a click/tap handler!
      permission = await Notification.requestPermission();
    } else {
      // Other browsers: can request anytime (but still better from user action)
      permission = await Notification.requestPermission();
    }
  } catch (err) {
    console.error("Error requesting notification permission:", err);
    toast.warn("Unable to request notification permission.");
    return false;
  }

  if (permission !== "granted") {
    toast.warn("Notifications disabled. You won’t receive alerts.");
    return false;
  }

  await subscribeUser(swRegistration, userId, restaurantId);
  return true; // Success
}

async function subscribeUser(swRegistration, userId, restaurantId) {
  const existing = await swRegistration.pushManager.getSubscription();
  if (existing) {
    // console.log("Subscription already exists.");
    toast.info("Notifications are already enabled.");
    console.log("Existing subscription:", existing);
    // return;
  }

  try {
    const vapidKey = process.env.REACT_APP_VAPID_PUBLIC_KEY;

    const rawSubscription = await swRegistration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidKey),
    });

    const subscription = {
      endpoint: rawSubscription.endpoint,
      p256dh: btoa(
        String.fromCharCode.apply(
          null,
          new Uint8Array(rawSubscription.getKey("p256dh"))
        )
      ),
      auth: btoa(
        String.fromCharCode.apply(
          null,
          new Uint8Array(rawSubscription.getKey("auth"))
        )
      ),
    };

    await fetch(`${process.env.REACT_APP_SECRET}/api/PWA/subscribe`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        subscription,
        userId,
        restaurantId,
      }),
    });

    // console.log("Push subscription saved successfully!");
    toast.success("Notifications have been enabled!");
  } catch (error) {
    console.error("Failed to subscribe the user: ", error);
    toast.error("Failed to enable notifications." + error);
  }
}

export async function unregisterPush(from, restaurantId) {
  if (!("serviceWorker" in navigator)) return;
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    if (subscription) {
      if (from === "logout") {
        await axios
          .post(`${process.env.REACT_APP_SECRET}/api/PWA/unsubscribe`, {
            endpoint: subscription.endpoint,
          })
          .then(async () => {
            console.log("Push subscription removed from backend.");
            const allRegistrations =
              await navigator.serviceWorker.getRegistrations();
            for (let reg of allRegistrations) {
              await reg.unregister();
              console.log("Service worker unregistered.");
            }
          })
          .catch((error) => {
            console.error(
              "Error removing push subscription from backend:",
              error
            );
          });

        await subscription.unsubscribe();
      } else {
        await axios
          .post(`${process.env.REACT_APP_SECRET}/api/PWA/updatesubscription`, {
            endpoint: subscription.endpoint,
            restaurantId: restaurantId,
          })
          .then(() => {
            console.log("Push subscription removed from backend.");
          })
          .catch((error) => {
            console.error(
              "Error removing push subscription from backend:",
              error
            );
          });
      }

      console.log("Push subscription removed.");
    }
  } catch (error) {
    alert("Error during push unregistration: " + error);
    console.error("Error during push unregistration:", error);
  }
}
